import { useState } from 'react';
import { ChevronsUpDown, Ticket, User } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { InvitationCodeDialog } from '@/components/invitation-code-dialog';
import { errorMessage } from '@/lib/api';
import { useActiveOrganization, useSetActiveOrganization } from '@/lib/queries';

const PERSONAL = '__personal__';

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter((w) => /^[\p{L}\d]/u.test(w))
    .map((w) => w[0])
    .slice(0, 3)
    .join('')
    .toUpperCase();
}

/**
 * Wisselt de actieve club (design-skill: ClubSwitcher). Clubneutraal: een
 * monochroom initialenmerk, nooit een clublogo of clubkleur.
 */
export function ClubSwitcher() {
  const { data, active } = useActiveOrganization();
  const setActive = useSetActiveOrganization();
  const [codeOpen, setCodeOpen] = useState(false);
  const label = active?.name ?? 'Persoonlijke modus';

  function onChange(value: string) {
    const organizationId = value === PERSONAL ? null : value;
    if (organizationId === (active?.id ?? null)) return;
    setActive.mutate(organizationId, {
      onError: (err) => toast.error(errorMessage(err)),
    });
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className="flex h-11 min-w-0 items-center gap-2 rounded-md px-2 text-sm font-medium outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Club wisselen, huidige: ${label}`}
        >
          <span
            aria-hidden="true"
            className={
              active
                ? 'grid size-7 shrink-0 place-items-center rounded-md bg-foreground text-[11px] font-bold text-background'
                : 'grid size-7 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground'
            }
          >
            {active ? initials(active.name) : <User className="size-3.5" />}
          </span>
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {(data?.organizations.length ?? 0) > 0 && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Je clubs
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup value={active?.id ?? PERSONAL} onValueChange={onChange}>
                {data?.organizations.map((o) => (
                  <DropdownMenuRadioItem key={o.id} value={o.id}>
                    {o.name}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuRadioGroup value={active?.id ?? PERSONAL} onValueChange={onChange}>
            <DropdownMenuRadioItem value={PERSONAL}>Persoonlijke modus</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          <DropdownMenuItem onSelect={() => setCodeOpen(true)}>
            <Ticket aria-hidden="true" />
            Uitnodiging invoeren
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <InvitationCodeDialog open={codeOpen} onOpenChange={setCodeOpen} />
    </>
  );
}
